import { IconButton } from "@serendie/ui";
import { BaseComponent } from "../../Core/BaseComponent/BaseComponent";
import { JSX } from "react";
import "./Styles.scss";
import React from "react";
import { SerendieSymbol } from "@serendie/symbols";
import { ILanguageValues, Localisation } from "@iconics/localisation";
import { Dialog } from "../../Dialogs/Dialog/Dialog";
import { DialogContent } from "../../Dialogs/Dialog/DialogContent";
import { Card } from "../../Cards/Card/Card";
import { GridContainer } from "../../Layout/Grid/GridContainer";
import { GridItem } from "../../Layout/Grid/GridItem";
import { TextField } from "../TextField/TextField";
import { DialogFooter } from "../../Dialogs/Dialog/DialogFooter";
import { ButtonBar } from "../../Buttons/ButtonBar/ButtonBar";
import { RightButtons } from "../../Buttons/ButtonBar/RightButtons";
import { Button } from "../../Buttons/Button/Button";

/**
 *  This input expects a __value__ which is the object representation of the following json format:
 *  {
 *    "en-GB": "Elevator",
 *    "es-ES": "Ascensor",
 *    "de-DE": "Aufzug",
 *    "ja-JP": "...."
 *  }
 *  it also provides a modal dialog that allows to modify the value in each available language.
 */
export class MultilingualTextField extends BaseComponent<IMultilingualTextFieldProps, IMultilingualTextFieldState>
{
    public static requiredLabel?: string;
    private currentLanguage: string;

    public static unknownLanguageLabel = '';
    public static dialogTitle = '';
    public static dialogSubTitle = '';
    public static okButtonLabel = '';
    public static cancelButtonLabel = '';

    constructor(props: IMultilingualTextFieldProps)
    {
        super(props);
        this.currentLanguage = this.props.localisation.languages.defaultLanguage;

        this.state =
        {
            open: false,
            values: {}
        };
    }

    public componentDidUpdate(prevProps: Readonly<IMultilingualTextFieldProps>): void
    {
        if (prevProps.value !== this.props.value)
        {
            this.setState({ values: { ...this.props.value } });
        }
    }

    private getLabel(languageCode: string): string
    {
        const language = this.props.localisation.languages.languages.find(i => i.code == languageCode);
        if (!language)
        {
            return this.props.unknownLanguageLabel ?? MultilingualTextField.unknownLanguageLabel;
        }
        return language.text();
    }

    private handleCurrentLanguageValueChanged(value: string): void 
    {
        const { values } = this.state;
        values[this.currentLanguage] = value;
        this.setState({ values });

        this.props.onChange(this.state.values);
    }

    private handleSingleValueChange(language: string, value: string)
    {
        const { values } = this.state;
        values[language] = value;
        this.setState({ values });
    }

    private handleSave(): void
    {
        this.setState({ open: false });
        this.props.onChange(this.state.values);
    }

    public render(): JSX.Element
    {
        return (
            <div className="serendie-multilingualtextfield">
                <TextField
                    {...this.props}
                    value={this.props.value[this.currentLanguage]}
                    onChange={e => this.handleCurrentLanguageValueChanged(e.target.value)}
                    required={this.props.required}
                    requiredLabel={this.props.requiredLabel ?? MultilingualTextField.requiredLabel}
                    
                />
                <IconButton
                    className="serendie-multilingualtextfield__iconbutton"
                    shape="rectangle"
                    aria-label={MultilingualTextField.dialogTitle}
                    styleType="outlined"
                    icon={<SerendieSymbol name={"type"} />}
                    onClick={() => this.setState({ values: { ...this.props.value }, open: true })}
                />
                {this.renderDialog()}
            </div>
        )
    }

    private renderDialog(): JSX.Element
    {
        // sort languages and set current language as first one
        const sortedLanguages = Object.entries(this.state.values).toSorted((a, b) =>
        {
            if (a[0] === this.currentLanguage) 
            {
                return -1;
            }
            if (b[0] === this.currentLanguage) 
            {
                return 1;
            }
            return a[0].localeCompare(b[0]);
        });

        return (
            <Dialog
                isOpen={this.state.open}
                onEscapeKeyDown={() => this.setState({ open: false })}
                zIndex={10000}
            >
                <DialogContent>
                    <Card
                        title={this.props.dialogTitle ?? MultilingualTextField.dialogTitle}
                        subTitle={this.props.dialogSubTitle ?? MultilingualTextField.dialogSubTitle}
                    >
                        <GridContainer>
                            {sortedLanguages.map(([key, value]) =>
                                <GridItem key={key}>
                                    <TextField
                                        required={this.props.required ? key === this.currentLanguage : false}
                                        label={this.getLabel(key)}
                                        placeholder={this.getLabel(key)}
                                        value={value}
                                        onChange={(e) => this.handleSingleValueChange(key, e.target.value)}
                                    />
                                </GridItem>
                            )}

                        </GridContainer>
                    </Card>
                </DialogContent>
                <DialogFooter>
                    <ButtonBar>
                        <RightButtons>
                            <Button
                                styleType='outlined'
                                onClick={() => this.setState({ open: false })}
                            >
                                {this.props.cancelButtonLabel ?? MultilingualTextField.cancelButtonLabel}
                            </Button>
                            <Button
                                styleType='filled'
                                onClick={() => this.handleSave()}
                            >
                                {this.props.okButtonLabel ?? MultilingualTextField.okButtonLabel}
                            </Button>
                        </RightButtons>
                    </ButtonBar>
                </DialogFooter>
            </Dialog>
        )
    }
}

export interface IMultilingualTextFieldProps extends Omit<React.ComponentProps<typeof TextField>, 'onChange' | 'value' | 'ref'>
{
    localisation: Localisation;
    unknownLanguageLabel?: string;
    dialogTitle?: string;
    dialogSubTitle?: string;
    okButtonLabel?: string;
    cancelButtonLabel?: string;
    onChange: (value: ILanguageValues) => void;
    value: ILanguageValues;
}

export interface IMultilingualTextFieldState 
{
    open: boolean;
    values: ILanguageValues;
}

